import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
//import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Helmet } from 'react-helmet';
import {
  Paper,
  Grid,
  CircularProgress,
  Fab,
  Button,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Section from 'components/Section';
import Tags from 'components/Tags';
import DocInfo from 'components/DocInfo';
import Identifier from 'components/Identifier';
import DocumentSide from 'components/DocumentSide';
import TableOfContent from 'components/TableOfContent';
import TimeLine from 'components/TimeLine';
import InfoBox from 'components/InfoBox';
import Siblings from 'components/Siblings';
import LawUpdatesBlock from 'components/LawUpdatesBlock';
import apiService from 'services/apiService';
import {
  capitalizeFirstLetter,
  cleanTitle,
  getDocumentUrl,
  getLausuntopalveluUrl,
  scrollToTop,
  formatDate,
  handleDisplayFab,
} from 'utils/commonTools';
import { translateKeyword, translateLabelField, translation, translateDayAndTime } from 'utils/translate';
import {
  reportError,
  clearError,
} from 'actions/errorActions';
import texts from 'texts';
import config from 'config';
import './Document.css';

import { changeLanguage } from 'actions/langActions';
import FinlexTreaty from './FinlexTreaty';
import { useLocation, useNavigate, useParams, Link} from 'react-router-dom';

const wordChars =
  'a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ0-9';

const MEDIA_TYPES = [
  'image/jpeg',
  'image/tiff',
  'video/mp4',
];

// Adds <em> tags around words found in termCounts
// termCount format is [{ word: 'nn', count: 1 }, {}...]
export const highlightContent = (source, termCounts) => {
  if (termCounts.length === 0) return source;

  // instead of using regex on source string directly
  // create a tree which can be traversed.
  // this allows skipping attributes altogether
  // without complicated regex (#126, #130).
  const parser = new DOMParser();
  const doc = parser.parseFromString(source, 'text/html');
  // walker for traversing the tree node by node
  const walker = doc.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_TEXT,
  );

  // extract the search words and create a regex pattern
  const words = termCounts.map((x) => x.word);
  // use negative lookbehind and negative lookahead
  // to match words or characters that are not in the middle of another word
  // e.g. highlighting HE skips heinä and virhe
  const pattern = new RegExp(
    String.raw`(?<![${wordChars}])(${words.join('|')})(?![${wordChars}])`,
    'gimu',
  );

  // extract all nodes with text from the tree before
  // making modifications. otherwise nextNode gets
  // messed up and starts missing elements
  const textNodes = [];
  let node;
  // eslint-disable-next-line no-cond-assign
  while ((node = walker.nextNode())) {
    textNodes.push(node);
  }

  // loop through the extracted nodes
//   for (const node of textNodes) {
//     const text = node.nodeValue;
//     if (pattern.test(text)){
//       pattern.lastIndex = 0; //reset the state
//     }

//     // create a temporary holder for the <em> tags
//     const span = doc.createElement('span');
//     span.innerHTML = text.replace(
//       pattern,
//       `<em class="highlight">$1</em>`,
//     );
//     // add <em> tags to the DOM
//     node.replaceWith(...span.childNodes);
//   }

//   // convert tree back to string
//   const output = doc.body.innerHTML;
//   return output;
// };

textNodes.forEach(node => {
  const text = node.nodeValue;
  if (pattern.test(text)){
    pattern.lastIndex = 0; // reset the state
  }

  // create a temporary holder for the <em> tags
  const span = doc.createElement('span');
  span.innerHTML = text.replace(
    pattern,
    `<em class="highlight">$1</em>`,
  );
  // add <em> tags to the DOM
  node.replaceWith(...span.childNodes);
  });

  const output = doc.body.innerHTML;
  return output;
}

const toggleHighlight = (isActive) => {
  /*
  DocumentSide.js has a checkbox. When it is toggled, this function runs.
  The function utilized inline styles and their priority over normal class styles.
  */
  if (isActive) {
    document.querySelectorAll("em.highlight").forEach(x => {
      // default to the normal styling
      x.style.background = "";
    })
  } else {
    document.querySelectorAll("em.highlight").forEach(x => {
      // override the default
      x.style.background = "none";
    });
  }
}

function useSizeObserver() {
  const [node, setNode] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const ref = useCallback(el => {
    setNode(el);
  }, []);

  useLayoutEffect(() => {
    if (!node) return;

    const observer = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        setSize({ width, height });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, size];
}


// MAIN COMPONENT
const Document = () => {
  const params = useParams()
  const location = useLocation()
  const docGroup = params?.doc_group?.replace('-', '_');
  const docID = params.doc_id;
  const dispatch = useDispatch();

  const [status, setStatus] = useState('initial');
  const [docData, setDocData] = useState({});
  const [sideData, setSideData] = useState({});
  const [timelineData, setTimelineData] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [boxRef, boxSize] = useSizeObserver();


  const lang = useSelector((store) => store.lang)
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0);
    // create a scroll listener that toggles the visibility of the Fab
    window.addEventListener('scroll', handleDisplayFab);
    // remove listener on unmount
    return () => window.removeEventListener('scroll', handleDisplayFab);
  }, []);

  useEffect(() => {
    const fetchDoc = async () => {
      setStatus((prev) =>
        prev === 'initial' ? 'loading' : 'loadingMore',
      );
      setDocData({});
      try {
        const result = await apiService.fetchData(
          `docs/${docGroup}/${docID}${location.search}`,
        );

        setDocData(result.doc);

        // check if there is timeline data in memory
        if (timelineData.length === 0) {
          const timeline = await apiService.fetchData(
            `timeline/${docGroup}/${docID}`,
          );
          if (timelineData) {
            setTimelineData(timeline);
          }
        }

        setSideData({
          wc: result.wc
            ? result.wc.map(({ word, count }) => ({
              word: capitalizeFirstLetter(word),
              count,
            }))
            : [],
          termCounts:
            result.terms && (result.terms.content || result.terms.content_sv)
              ? result.terms.content || result.terms.content_sv
              : [],
          innerHits: result.inner_hits || [],
          meta: result.project_meta,
        });
        setStatus('ready');
        dispatch(clearError());
      } catch (err) {
        dispatch(reportError(err));
      }
    };
    fetchDoc();
    // TODO: refactor this effect hook to enable the use of exhaustive deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, docGroup, docID, lang]);

  // Add sibling (same document type of the current document) data when timeline is loaded or docID changes
  useEffect(() => {
    if (
      [
        'lausuntokierros',
        'lausuntokierros_muut',
        'lausuntokierros_lausunnot',
        'valiokunta_asiakirjat',
        'he_asiantuntijalausunnot',
        'poytakirja',
      ].includes(docGroup)
    ) {
      setSiblings(
        timelineData
          .flatMap((item) =>
            docGroup === 'lausuntokierros'
              ? [
                ...item.lausuntokierros_lausunnot,
                ...item.lausuntokierros_muut,
                ...item.lausuntopalvelu_lausunnot,
              ]
              : docGroup === 'lausuntokierros_lausunnot'
                ? [
                  ...item.lausuntokierros_lausunnot,
                  ...item.lausuntopalvelu_lausunnot,
                ]
                : item[docGroup],
          )
          .filter((item) => Object.keys(item).length !== 0)
          .map((item) => ({
            id: item.doc_id || item.id,
            date:
              item.date ||
              item.asettamis_paiva ||
              item.laadittu ||
              item.vahvistettu ||
              item.laatimispaiva ||
              item.published_on ||
              item.signed_at,
            closingDate: item.closing_date,
            title: cleanTitle(
              docGroup === 'lausuntokierros_muut' ||
                docGroup === 'lausuntokierros'
                ? item.closing_date || item.deadline
                  ? `Lausuntopalvelun lausunnot (${item.lausunnot})`
                  : [item.laatija, translateKeyword(item.tyyppi, lang)]
                    .filter((i) => i)
                    .join(', ')
                : item.laatija || translateLabelField(docGroup, item, 'title', lang),
            ),
            link: item.closing_date || item.deadline
              ? getLausuntopalveluUrl(item.id)
              : getDocumentUrl(
                docGroup,
                item.doc_id || item.id,
                lang
              ),
          }))
          // '99's for sorting null dates to end
          .sort((a, b) => {
            const dateSortResult = (
              a.date || '99'
            ).localeCompare(b.date || '99');
            if (dateSortResult !== 0) return dateSortResult;
            // secondarily sort by title
            // sort nulls to the end, and ensure the values exist
            if (!a?.title) return 1;
            if (!b?.title) return -1;
            return a.title.localeCompare(b.title);
          })
          // sort the sibling with time range (closingDate) to first in the list
          .reduce((acc, element) => {
            if (element.closingDate) {
              return [element, ...acc];
            }
            return [...acc, element];
          }, []),
      );
    } else {
      setSiblings([]);
    }
  }, [docGroup, timelineData, lang]);

  // Format text to html
  function formatContent(content) {
    return {
      __html: !sideData.termCounts
        ? content
        : highlightContent(content, sideData.termCounts),
    };
  }

  // InfoBox in case content is not available
  function renderNoContent() {
    return (
      <InfoBox>
        {docGroup.startsWith('lausuntokierros') ? (
          <i>{texts.info.lausunnot_no_content[lang]}</i>
        ) : (
          <i>{translation("document", "no_content", lang)}</i>
        )}
      </InfoBox>
    );
  }

  function goToFinnishVersion() {
    const url = new URL(window.location);
    url.searchParams.set("lang", "fi");
    navigate(url.search);
    dispatch(changeLanguage("fi"))
  }

  function renderNoContentInSwedish(){
    return (
      <InfoBox>
        {docGroup.startsWith('lausuntokierros') ? (
          <i>{texts.info.lausunnot_no_content[lang]}</i>
        ) : (
          <>
            <i>{translation("document", "no_content_in_swedish", lang)}</i>
            <div>
              <Button 
              sx={{textAlign:'left',
                  color: '#2564ec',
                  textDecorationStyle: 'italic',
                  fontStyle: 'italic',
                  paddingLeft: '0rem',}}
              onClick={goToFinnishVersion} >
              Visa dokumentet på finska
              </Button>
            </div>
          </>
        )}
      </InfoBox>
    );
  }

  if (status === 'loading') {
    return (
      <Section className="section--document">
        <Grid container spacing={4}>
          {/* Show throbber here when loading initial document */}
          <div className="progress-wrapper fade-in">
            <CircularProgress size={68} />
          </div>
        </Grid>
      </Section>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {docData._source
            ? lang === "fi" ?`${
            docData._source.tunnus ||
            docData._source.doc_id ||
            docData._source.title
            } | ${config.site.name}`
            : 
            `${
            docData._source.tunnus_sv ||
            docData._source.doc_id ||
            docData._source.title_sv
            } | ${config.site.name}`
            : config.site.name}
        </title>
      </Helmet>

      <Section ref={boxRef} className="section--timeline" >
        {timelineData.map((timeline) => (
          <TimeLine
            key={
              timeline.hallituksen_esitykset[0]
                ? timeline.hallituksen_esitykset[0].doc_id
                : 'he_missing'
            }
            data={timeline}
            activeDoc={docID}
            //docGroup={docGroup}
            docData={docData}
          />
        ))}
      </Section>

      <Section className="section--document">
        <Grid
          container
          spacing={4}
          direction='row'
          sx = {{justifyContent: "end"}}
        >
          {status === 'ready' && docData._source.toc && (
            <Grid item xs={12} sm={12} md={3} lg={3} >
              <TableOfContent toc={docData?._source?.toc} size={boxSize} />
            </Grid>
          )}
          {status === 'loadingMore' ? (
            /* Show throbber here when loading new document */
            <div className="progress-wrapper fade-in">
              <CircularProgress size={68} />
            </div>
          ) : (
            <>
              <Grid item xs={12} sm={12} md={docData?._source?.toc? 9: 12} lg={docData?._source?.toc? 6: 9}>
                {!!docData._source && (
                  <Paper elevation={2}>
                    <main
                      id="document"
                      className="document"
                    >
                      <header>
                        {!!(
                          docData._source.tunnus ||
                          docData._source.tunnus_sv ||
                          docData._source.tyyppi
                        ) && (
                            <Identifier>
                              {lang === "fi" ? docData._source.tunnus : docData._source.tunnus_sv?docData._source.tunnus_sv : docData._source.tunnus ||
                                translation(
                                  docData._source.tyyppi, lang
                                ).toUpperCase()}
                            </Identifier>
                          )}
                        <DocInfo
                          fields={config.docGroups[
                            docGroup
                          ].infoFields.map((field) => [
                            field,
                            docData._source[field],
                          ])}
                        />
                        <h1 className='title'>{lang === "fi" ? docData._source.title: docData._source.title_sv?docData._source.title_sv: translateDayAndTime(docGroup, docData._source.title, lang)}</h1>
                        {/* Render fields listed in docGroup's tagFields in config */}
                        {config.docGroups[docGroup]
                          .tagFields &&
                          config.docGroups[
                            docGroup
                          ].tagFields[lang].map(
                            (field) =>
                              docData._source[field] && (
                                <Tags
                                  key={field}
                                  title={translation("results", field, lang)}
                                  tagList={
                                    docData._source[field]
                                  }
                                />
                              ),
                          )}
                        {/* Render link to original document */}
                        {!!docData._source.url && (
                          <InfoBox>
                            {docData._source.source === 'Finlex API' ? 
                              <>
                                <div className="arrow-link">
                                  <ArrowRightIcon color="primary" />
                                  <a
                                    href={(lang === 'se' && docData._source.url_sv ? docData._source.url_sv : docData._source.url)}
                                    target="_BLANK"
                                    rel="noopener noreferrer"
                                  >
                                    {translation("document", "näytä säädöskokoelmassa", lang)}
                                  </a>
                                </div>
                              </>
                              :
                              <>
                                <i>{texts.info.machineread[lang]}</i>
                                <div className="arrow-link">
                                  <ArrowRightIcon color="primary" />
                                  <a
                                    href={docData._source.url}
                                    target="_BLANK"
                                    rel="noopener noreferrer"
                                  >
                                    {translation("document", "avaa/lataa alkuperäinen dokumentti", lang)}
                                  </a>
                                </div>
                              </>
                            }
                          </InfoBox>
                        )}
                        {docData._source.source !== 'Finlex API' && !!docData._source.year && parseInt(docData._source.year, 10) < 1991 && (
                          <InfoBox>
                            <i>{texts.info.machineread_older_text[lang]}</i>
                          </InfoBox>
                        )}
                      </header>
                      {
                        docData._source.source === 'Finlex API' ? 
                          <FinlexTreaty docData={docData} lang={lang} sideData={sideData} highlightContent={highlightContent} />     
                        :
                          (MEDIA_TYPES.includes(
                              docData._source.content_type,
                            ) ? (
                              <>
                                <embed
                                  src={docData._source.url}
                                  type={
                                    docData._source.content_type
                                  }
                                  width="700"
                                  height="auto"
                                ></embed>
                              </>
                            ) : docData._source.content ? (
                              <div
                                className="doc-content"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={formatContent(
                                  docData._source.content
                                )}
                              />
                            ) : (!docData._source.content && lang === "fi") ? 
                              (
                                  <div className="doc-content">
                                    {renderNoContent()}
                                  </div>
                              ):
                              (
                                  <div>
                                    {renderNoContentInSwedish()}
                                  </div>
                              )
                            )
                        
                      }
                    </main>
                  </Paper>
                )}

                {/* Show highlighted inner hits under the document (lausuntokierros -specifig) */}
                {!!sideData?.innerHits?.length && (
                  <Paper
                    elevation={2}
                    className="inner-hit-paper"
                  >
                    {sideData.innerHits.map(
                      (innerHit) =>
                        !!innerHit.highlight && (
                          <div
                            key={innerHit._source.id}
                            className="inner-hit--container"
                          >
                            <h3 className="inner-hit--title">
                              <Link
                                to={`/asiakirjat/lausuntokierros-asiakirjat/${innerHit._source.id}${location.search}`}
                              >
                                {innerHit._source.title}
                              </Link>
                            </h3>
                            <div className="inner-hit--meta">
                              <p>
                                {formatDate(
                                  innerHit._source
                                    .laatimispaiva,
                                )}
                              </p>
                              <p>
                                {translation("document", "tyyppi", lang)}:{' '}
                                {translateKeyword(
                                  innerHit._source.tyyppi, lang
                                ).toUpperCase()}
                              </p>
                              <p>
                                {translation("document", "laatija", lang)}:{' '}
                                {innerHit._source.laatija}
                              </p>
                              {innerHit._source.url ? (
                                <a
                                  href={
                                    innerHit._source &&
                                    innerHit._source.url
                                  }
                                >
                                  {translation("document", "original", lang)}
                                </a>
                              ) : (
                                <i>
                                  {translation("document", "no_original", lang)}
                                </i>
                              )}
                            </div>
                            {!!innerHit.highlight &&
                              innerHit.highlight[
                                'asiakirjat.content'
                              ].map((highlight, idx) => (
                                <div
                                  className="inner-hit--snippets"
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={idx}
                                >
                                  <span
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                      __html: `...${highlight}... `,
                                    }}
                                  />
                                </div>
                              ))}
                            {!!innerHit.error && (
                              <div>{innerHit.error}</div>
                            )}
                          </div>
                        ),
                    )}
                  </Paper>
                )}
              </Grid>
            </>
          )}

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={3}
            role="complementary"
          >
            {status === 'ready' && (
              <DocumentSide
                sideData={sideData}
                toggleHighlight={toggleHighlight}
              />
            )}
            {!!timelineData[0]?.laws?.length && (
              <LawUpdatesBlock
                laws={timelineData[0]?.laws}
                lang={lang}
              />
            )}
            {!!siblings.length && (
              <Siblings
                data={siblings}
                docID={docID}
                docGroup={docGroup}
                lang={lang}
              />
            )}
          </Grid>
        </Grid>
        <Fab
          variant="circular"
          size="medium"
          color="secondary"
          aria-label="add"
          className='fab-navigation fab-hide'
          onClick={() => scrollToTop()}
        >
          {/* <NavigationIcon /> */}
          <ArrowUpwardIcon />
        </Fab>
      </Section>
    </>
  );
};

export default Document;
